import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Select,
  MenuItem,
  TextField,
  Button,
  Tooltip,
  styled,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import BasicMenu from '../components/BasicMenu';
import { AmbienteExterno, TipoAmbienteExterno } from '../types/negocio/AmbienteExterno';
import { ambienteExternoService } from '../services/AmbienteExternoService';
import toastService from '../utils/toastService';
import SaveStatusIndicator, { SaveStatus } from '../components/SaveStatusIndicator';
import LoadingIndicator from './LoadingIndicator';
import { CLASSIFICACOES_AMBIENTE_EXTERNO } from '../types/constants/ClassificacaoAmbienteExterno';
import InfoPopover from './InfoPopover';
import PrimaryButton from './PrimaryButton';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#042E4B',
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

interface TabelaPESTELProps {
  tipo: TipoAmbienteExterno;
}

const TabelaPESTEL: React.FC<TabelaPESTELProps> = (props) => {
  const [rows, setRows] = useState<AmbienteExterno[]>([]);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [savingRow, setSavingRow] = useState<number | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setSaveStatus?.('saving');
      try {
        const data = await ambienteExternoService.listarTodosPorTipo(props.tipo);
        setRows(data);
        setSaveStatus?.('saved');
        setTimeout(() => setSaveStatus?.('idle'), 3000);
      } catch (error) {
        console.error('Error fetching data:', error);
        toastService.error('Erro ao carregar indicadores');
        setSaveStatus?.('error');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [props.tipo]);

  const handleChange = async <K extends keyof AmbienteExterno>(
    index: number,
    field: K,
    value: AmbienteExterno[K],
    save: boolean = true
  ) => {
    const updated = [...rows];
    (updated[index] as any)[field] = value;

    updated[index].pontuacao =
      updated[index].impacto *
      updated[index].probabilidade *
      updated[index].tendencia;

    if (save) {
      setSavingRow(index);
      setSaveStatus('saving');
      await salvar(updated[index]);
      setSavingRow(null);
    }

    setRows(updated);
  };

  const handleAddRow = async () => {
    const newRow: AmbienteExterno = {
      guid: undefined,
      tipo: props.tipo,
      opcao: '',
      fator: 'Oportunidade',
      impacto: 1,
      probabilidade: 1,
      tendencia: 1,
      pontuacao: 1,
      ordem: rows.length + 1,
      ativo: true,
    };

    const newRows = [...rows, newRow];
    setRows(newRows);

    const newIndex = newRows.length - 1;
    setEditingRow(newIndex);

    setTimeout(() => {
      const el = rowRefs.current[newIndex];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  };


  const handleDeleteRow = async (index: number) => {
    const rowToDelete = rows[index];
    const newRows = [...rows];
    newRows.splice(index, 1);
    try {
      setSaveStatus('saving');
      await ambienteExternoService.deletar(rowToDelete.guid!);
      setRows(newRows);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error deleting row:', error);
      toastService.error('Erro ao excluir indicador');
      setSaveStatus('error');
    }
  };

  const handleDeleteAll = async () => {
    setOpenConfirmDialog(false);
    setSaveStatus('saving');
    try {
      // TODO: criar endpoint deletarTodos para este tipo (props.tipo)
      // await ambienteExternoService.deletarTodosPorTipo(props.tipo);
      setRows([]);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error deleting all rows:', error);
      toastService.error('Erro ao excluir todos os indicadores');
      setSaveStatus('error');
    }
  };

  const salvar = async (row: AmbienteExterno) => {
    try {
      row.guid = (await ambienteExternoService.save(row)).guid;
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error saving row:', error);
      toastService.error('Erro ao salvar indicador');
      setSaveStatus('error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'end', gap: 2 }}>
          <Tooltip title="Adicionar um novo Ponto Forte">
            <PrimaryButton onClick={handleAddRow}>
              + Adicionar
            </PrimaryButton>
          </Tooltip>
          <Tooltip title="Excluir todos os Ponto Forte">
            <PrimaryButton variant="outlined" color="error" onClick={() => setOpenConfirmDialog(true)}>
              Excluir Tudo
            </PrimaryButton>
          </Tooltip>
        </Box>
        <SaveStatusIndicator status={saveStatus} />
      </Box>

      <Paper elevation={3} sx={{ mt: 2 }}>
        <TableContainer sx={{ borderRadius: '8px' }}>
          <Table aria-label="pestel table">
            <TableHead>
              <TableRow>
                <StyledTableCell sx={{ width: '50%' }}><strong>Opção</strong></StyledTableCell>
                <StyledTableCell align="center" sx={{ width: '12%' }}>
                  <InfoPopover
                    title="Fator"
                    description="
                      Oportunidades:
                      Fatores externos e positivos que favorecem o crescimento e os objetivos da empresa, permitindo antecipar tendências e gerar vantagens competitivas.
                      Ameaças:
                      Fatores externos e negativos que podem dificultar o desempenho; reconhecê-los ajuda a reduzir riscos e preparar estratégias defensivas.
                    "
                    placement="top"
                  >
                    <strong>Fator</strong>
                  </InfoPopover>
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ width: '12%' }}>
                  <InfoPopover
                    title="Impacto"
                    description="Quanto este indicador realmente tem impacto sobre o resultado da empresa."
                    placement="top"
                  >
                    <strong>Impacto</strong>
                  </InfoPopover>
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ width: '12%' }}>
                  <InfoPopover
                    title="Chance"
                    description="Quais as reais chances deste indicador, oportunidade ou ameaça, vir a acontecer na prática."
                    placement="top"
                  >
                    <strong>Chance</strong>
                  </InfoPopover>
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ width: '12%' }}>
                  <InfoPopover
                    title="Tendência"
                    description="Refere-se à evolução provável do indicador ao longo do tempo, caso nenhuma ação seja tomada. Analisa se o indicador tende a ampliar, permanecer estável ou até regredir."
                    placement="top"
                  >
                    <strong>Tendência</strong>
                  </InfoPopover>
                </StyledTableCell>
                <StyledTableCell align="center"><strong>Pontuação</strong></StyledTableCell>
                <StyledTableCell align="center"><strong>Ações</strong></StyledTableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <LoadingIndicator message="Carregando indicadores..." />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1">Nenhum indicador encontrado.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => (
                  <StyledTableRow key={index}>
                    <StyledTableCell component="th" scope="row">
                      {editingRow === index ? (
                        <>
                          <TextField
                            fullWidth
                            multiline
                            minRows={1}
                            variant="standard"
                            autoFocus
                            value={row.opcao}
                            onChange={(e) => handleChange(index, 'opcao', e.target.value, false)}
                            onKeyDown={async (e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                setSavingRow(index);
                                await salvar(row);
                                setSavingRow(null);
                                setEditingRow(null);
                              }
                            }}
                            onBlur={async () => {
                              if (editingRow === index) {
                                setSavingRow(index);
                                await salvar(row);
                                setSavingRow(null);
                                setEditingRow(null);
                              }
                            }}
                            sx={{
                              '& .MuiInputBase-input': {
                                fontSize: 16,
                                cursor: 'text',
                                padding: '4px 0px',
                              },
                            }}
                          />
                          <Typography sx={{ color: 'gray', fontSize: '0.75rem', mt: 1 }}>
                            Pressione "Enter" ou clique fora para confirmar
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: 16 }}>
                          {row.opcao}
                        </Typography>
                      )}
                    </StyledTableCell>

                    <StyledTableCell align="center">
                      <Select
                        value={row.fator}
                        onChange={(e) => handleChange(index, 'fator', e.target.value)}
                        size="small"
                        disabled={savingRow === index}
                      >
                        {CLASSIFICACOES_AMBIENTE_EXTERNO.map((value) => (
                          <MenuItem key={value} value={value}>
                            {value}
                          </MenuItem>
                        ))}
                      </Select>
                    </StyledTableCell>

                    {(['impacto', 'probabilidade', 'tendencia'] as (keyof AmbienteExterno)[]).map((campo) => (
                      <StyledTableCell align="center" key={campo}>
                        <Select
                          value={row[campo]}
                          onChange={(e) => handleChange(index, campo, parseInt(e.target.value as string))}
                          size="small"
                          disabled={savingRow === index}
                        >
                          {[1, 2, 3, 4].map((value) => (
                            <MenuItem key={value} value={value}>
                              {value}
                            </MenuItem>
                          ))}
                        </Select>
                      </StyledTableCell>
                    ))}

                    <StyledTableCell align="center">
                      <Typography variant="body2">
                        {row.pontuacao}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell align="center">
                      <BasicMenu
                        onEdit={() => setEditingRow(index)}
                        onDelete={() => handleDeleteRow(index)}
                      />
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog de confirmação */}
      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza de que deseja excluir todos os indicadores? Esta ação não poderá ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <PrimaryButton
            variant="outlined" color="error"
            onClick={() => setOpenConfirmDialog(false)}
          >
            Cancelar
          </PrimaryButton>
          <PrimaryButton onClick={handleDeleteAll}>
            Confirmar
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TabelaPESTEL;
